import posthog, {
  type BeforeSendFn,
  type CaptureResult,
  type Properties,
} from "posthog-js";
import { silently } from "./analytics";

/**
 * Query parameters that carry personal data or user-written text: the list
 * search (`q`) and the invite link's email address and one-time code.
 */
const SENSITIVE_PARAMS = ["q", "email", "code"];

/** A shared tender link carries its access token in the path. */
const SHARE_TOKEN_PATH = /\/share\/tender\/[^/?#]+/;

/**
 * PostHog's URL-like properties: `$current_url`, `$referrer`, `$pathname`,
 * their `$initial_*` person copies, `$prev_pageview_*`, `$session_entry_url`.
 */
const URL_KEY = /url|referrer|pathname/i;

/**
 * Removes the share token and the sensitive query parameters from a URL or
 * path. The regex has no `/g` flag on purpose: a shared global regex keeps
 * `lastIndex` between calls and silently skips every other match.
 */
export const scrubUrl = (url: string): string => {
  const path = url.replace(SHARE_TOKEN_PATH, "/share/tender/:token");
  const queryStart = path.indexOf("?");
  if (queryStart === -1) return path;

  const hashStart = path.indexOf("#", queryStart);
  const hash = hashStart === -1 ? "" : path.slice(hashStart);
  const params = new URLSearchParams(
    path.slice(queryStart + 1, hashStart === -1 ? undefined : hashStart),
  );
  if (!SENSITIVE_PARAMS.some((param) => params.has(param))) return path;

  SENSITIVE_PARAMS.forEach((param) => params.delete(param));
  const query = params.toString();

  return `${path.slice(0, queryStart)}${query ? `?${query}` : ""}${hash}`;
};

/** Pageviews nest the person copies (`$set_once`) a level down, so it recurses. */
const scrubUrlProperties = (properties: Properties): Properties =>
  Object.fromEntries(
    Object.entries(properties).map(([key, value]) => {
      if (typeof value === "string" && URL_KEY.test(key)) {
        return [key, scrubUrl(value)];
      }
      if (value && typeof value === "object" && !Array.isArray(value)) {
        return [key, scrubUrlProperties(value as Properties)];
      }
      return [key, value];
    }),
  );

/**
 * Scrubs every URL-like property before the event leaves the browser, on the
 * event and on the `$set` / `$set_once` person copies. Drops the event when
 * scrubbing fails, so personal data never goes out unscrubbed.
 */
export const redactShareTokens: BeforeSendFn = (event) => {
  let scrubbed: CaptureResult | null = null;

  silently(() => {
    if (!event) return;
    scrubbed = {
      ...event,
      properties: scrubUrlProperties(event.properties ?? {}),
      ...(event.$set ? { $set: scrubUrlProperties(event.$set) } : {}),
      ...(event.$set_once
        ? { $set_once: scrubUrlProperties(event.$set_once) }
        : {}),
    };
  });

  return scrubbed;
};

/**
 * Starts PostHog whenever a project key is configured, in any build mode, so
 * events can be checked in the development project before they reach
 * production. Local and dev environments carry the development project's key.
 * Pageviews are sent by `trackPageviews`, because PostHog's own pageview
 * capture only sees the first load of this single-page app.
 */
export const initAnalytics = (): void =>
  silently(() => {
    const key = import.meta.env.VITE_POSTHOG_KEY;
    if (!key) return;

    posthog.init(key, {
      api_host: "https://eu.i.posthog.com",
      person_profiles: "identified_only",
      // `href` is dropped because mailto: links carry contact emails, which
      // mask_all_element_attributes does not hide from `$elements_chain`.
      autocapture: { element_attribute_ignorelist: ["href"] },
      mask_all_text: true,
      mask_all_element_attributes: true,
      capture_pageview: false,
      capture_pageleave: true,
      // Heatmap data is keyed by the full location.href, which before_send
      // never sees.
      capture_heatmaps: false,
      // The /flags request sends the stored landing URL as person properties,
      // bypassing before_send. The app uses no PostHog feature flags; turn
      // them on only together with a check of that request's payload.
      advanced_disable_feature_flags: true,
      mask_personal_data_properties: true,
      custom_personal_data_properties: SENSITIVE_PARAMS,
      property_denylist: [
        // The page title holds user-written names, such as a view's title.
        "title",
        "$geoip_latitude",
        "$geoip_longitude",
        "$geoip_postal_code",
      ],
      before_send: redactShareTokens,
    });
  });
